import type { pisti } from "@app/rules";
import type { CSSProperties } from "react";
import { useT } from "../i18n/context";
import { Card, CardBack, cardLabel, rankLabel, SUIT_GLYPH } from "./Card";
import { CaptureNote, FirstCaptureRow, TakenFlight } from "./PistiCapture";
import { PistiCount } from "./PistiCount";
import { playerOfTop, type Slots, slotOf, TOWARD, tiltOf } from "./pistiPileParts";
import { useCoveredCard, usePileSweep } from "./usePileSweep";

type Size = { w: number; h: number };

/** The three face-down cards lie fanned under the pile until somebody takes them. */
const FAN = [-14, 0, 13];
/** Cards under the top one, drawn as edges, deepest first: enough to read "a pile", never what is in it. */
const UNDER = [4, 3, 2, 1];

/**
 * The card on top: thrown in from its player's side, then lying upright with a pile's small tilt
 * (owner, 20 Sept 2026: no need to turn it toward the player; where it came from says who played
 * it). A capture lays it beside the card it matched.
 */
function TopCard({
  top,
  resting,
  from,
  card,
}: {
  top: NonNullable<pisti.PistiView["pileTop"]>;
  resting: boolean;
  from: keyof Slots | null;
  card: Size;
}) {
  const k = card.w / 72;
  const come = from === null ? { dx: 0, dy: 0 } : TOWARD[from];
  const vars = {
    "--rot": `${tiltOf(cardLabel(top))}deg`,
    "--dx": `${come.dx * k * 1.6}px`,
    "--dy": `${come.dy * k * 1.6}px`,
  } as CSSProperties;
  return (
    <div
      className="absolute inset-0 transition-[translate] duration-200"
      style={{ translate: resting ? `${card.w * 0.34}px 0` : "0 0" }}
    >
      <div
        key={cardLabel(top)}
        className={`animate-throw ${resting ? "rounded-[8px] ring-2 ring-gold" : ""}`}
        style={vars}
      >
        <Card card={top} width={card.w} height={card.h} state="plain" />
      </div>
    </div>
  );
}

function Stack({
  view,
  card,
  from,
}: {
  view: pisti.PistiView;
  card: Size;
  from: keyof Slots | null;
}) {
  const top = view.pileTop;
  const sweep = view.sweep;
  const covered = useCoveredCard(view);
  const under = Math.min(Math.max(view.pileCount - view.faceDownCount - 1, 0), 4);
  const k = card.w / 72;
  return (
    <div className="relative" style={{ width: card.w, height: card.h }}>
      {FAN.slice(0, view.faceDownCount).map((tilt) => (
        <div key={tilt} className="absolute inset-0" style={{ rotate: `${tilt}deg` }}>
          <CardBack width={card.w} height={card.h} />
        </div>
      ))}
      {UNDER.filter((depth) => depth <= under).map((depth) => (
        <div
          key={depth}
          className="absolute inset-0 rounded-[8px] border border-edge bg-ink/85 shadow-pill"
          style={{ translate: `${-depth * 1.5 * k}px ${-depth * 1.2 * k}px` }}
        />
      ))}
      {sweep === null ? null : (
        // The card that was matched lies beside the one that took it: the capture explains itself.
        <div
          className="absolute inset-0"
          style={{ translate: `${-card.w * 0.42}px 0`, rotate: "-9deg" }}
        >
          <Card card={sweep.matched} width={card.w} height={card.h} state="plain" />
        </div>
      )}
      {covered === null ? null : (
        // The card being covered stays until the new one has landed, so the pile is never a blank.
        <div className="absolute inset-0" style={{ rotate: `${tiltOf(cardLabel(covered))}deg` }}>
          <Card card={covered} width={card.w} height={card.h} state="plain" />
        </div>
      )}
      {top === null ? null : <TopCard top={top} resting={sweep !== null} from={from} card={card} />}
    </div>
  );
}

/**
 * What lies in the middle: the pile, or, when a first capture is resting and this seat's team took
 * it, the five cards of that capture laid out in a row.
 */
function Middle({
  view,
  card,
  from,
}: {
  view: pisti.PistiView;
  card: Size;
  from: keyof Slots | null;
}) {
  const { sweep, pileTop, faceDown } = view;
  if (sweep?.first === true && faceDown !== null && pileTop !== null)
    return (
      <FirstCaptureRow hidden={faceDown} matched={sweep.matched} taker={pileTop} card={card} />
    );
  return <Stack view={view} card={card} from={from} />;
}

/** "5 cards on the table, ♥7 on top", for whoever cannot see the pile. */
function usePileLabel(view: pisti.PistiView): string {
  const top = view.pileTop;
  const { t } = useT();
  if (top === null) return t("pisti.pile.empty");
  const card = `${SUIT_GLYPH[top.suit]}${rankLabel(top.rank)}`;
  return t("pisti.pile.label", { count: view.pileCount, card });
}

/**
 * Under the pile, the only line the table keeps there: what the deck still holds and which of its
 * three deals is being played. How many cards lie in the pile is read off the pile itself, and who
 * did what is said as it happens (owner, 20 Sept 2026: "Bot 2 played" and "5 on the table" are
 * detail nobody needs).
 */
function DeckLine({ view, card }: { view: pisti.PistiView; card: Size }) {
  const { t } = useT();
  const size = Math.max(12, Math.round(13 * (card.w / 72)));
  return (
    <span className="text-legible font-bold text-ink/80" style={{ fontSize: size }}>
      {t("pisti.stock", {
        count: view.stockCount,
        round: view.dealRound,
        rounds: view.dealRounds,
      })}
    </span>
  );
}

/**
 * Pişti's middle (review 0029 S12-S14): the pile with only its top card showing, the face-down
 * cards under it until the first capture, and what the deck still holds; who took the pile, with
 * what, and "Pişti!" when it is one; the deck's count between two decks.
 */
export function PistiPile({
  view,
  card,
  slots,
  names,
}: {
  view: pisti.PistiView;
  card: Size;
  slots: Slots;
  names: readonly (string | null)[];
}) {
  const label = usePileLabel(view);
  const sweep = usePileSweep(view);
  if (view.phase !== "playing") return <PistiCount view={view} card={card} />;
  const player = playerOfTop(view);
  const lifted = view.sweep !== null && sweep !== null && sweep.stage !== "rest";
  return (
    <div
      className="relative flex h-full flex-col items-center justify-center"
      style={{ gap: Math.round(card.h * 0.13) }}
    >
      <div className="relative">
        {/* Above the pile, so the cards never move when a capture is announced. */}
        <div className="absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2">
          <CaptureNote view={view} names={names} />
        </div>
        <div role="img" aria-label={label} style={{ width: card.w, height: card.h }}>
          {/* Once the pile is in the air the table is empty, though the server still holds it. */}
          {lifted ? null : <Middle view={view} card={card} from={slotOf(slots, player)} />}
        </div>
      </div>
      <TakenFlight sweep={sweep} card={card} slots={slots} />
      <DeckLine view={view} card={card} />
    </div>
  );
}
